import { BlobServiceClient } from '@azure/storage-blob';
import dayjs from 'dayjs';
import { logging } from './log';

const sasToken = process.env.REACT_APP_AZURE_BLOB_SAS_TOKEN;

export const CONFIG_CONTAINER_NAME = {
  UPLOADS: process.env.REACT_APP_AZURE_BLOB_CONTAINER,
  APP_ASSETS_HOME: process.env.REACT_APP_AZURE_BLOB_CONTAINER_APP_ASSETS_HOME,
  AZURE_BLOB_CONTAINER_APP_ASSETS: process.env.REACT_APP_AZURE_BLOB_CONTAINER_APP_ASSETS,
};

const azureCDN = process.env.REACT_APP_AZURE_CDN;

const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

export const isStorageConfigured = () => {
  return !(!storageAccountName || !sasToken);
};

const createBlobInContainer = async (
  containerClient: any,
  file: any,
  blobPath: any,
  fileType: string,
  containerName: string,
) => {
  try {
    if (!file) {
      throw new Error('Upload image to azure failed');
    }
    const blobClient = containerClient.getBlockBlobClient(blobPath);
    const options = {
      blobHTTPHeaders: { blobContentType: fileType },
    };

    await blobClient.uploadBrowserData(file, options);
    await blobClient.setMetadata({ UserName: 'anonymous' });
    return `https://${storageAccountName}.blob.core.windows.net/${containerName}/${blobPath}`;
  } catch (error) {
    logging(error);
    throw new Error('Upload image to azure failed');
  }
};

const getBlobPath = (url: any) => {
  if (!url) return null;
  let blobPath = '';
  if (azureCDN) {
    blobPath = url.replace(`https://${azureCDN}/${CONFIG_CONTAINER_NAME.UPLOADS}/`, '');
  } else {
    blobPath = url.replace(`https://${storageAccountName}.blob.core.windows.net/${CONFIG_CONTAINER_NAME.UPLOADS}/`, '');
  }
  return blobPath;
};

export const deleteBlob = async (link: any, containerName?: string) => {
  try {
    const blobPath = getBlobPath(link);
    if (!blobPath || !containerName) return false;
    const blobService = new BlobServiceClient(`https://${storageAccountName}.blob.core.windows.net/?${sasToken}`);
    const containerClient = blobService.getContainerClient(containerName);
    const blobClient = containerClient.getBlockBlobClient(blobPath);
    await blobClient.delete();
    return true;
  } catch (err) {
    logging(err);
    return false;
  }
};

export type UploadFileToBlobInterface = {
  dataURL?: any;
  blob?: any;
  fileType?: string;
  fileName?: string;
  isImageURL?: boolean;
  isVideoURL?: boolean;
  isLogo?: boolean;
};

const formatFileName = (fileName: string) => {
  const specialChars = /[`!@#$%^&*()\-+={};':"\\|,<>?~ ]/g;
  return fileName.replace(specialChars, '_');
};

export const uploadFileToBlob = async (dataImage: UploadFileToBlobInterface[], containerName?: string) => {
  try {
    if (!dataImage || !dataImage.length || !containerName) return [];
    const blobService = new BlobServiceClient(`https://${storageAccountName}.blob.core.windows.net/?${sasToken}`);
    const containerClient = blobService.getContainerClient(containerName);

    const promisesUploadImage = dataImage.map((data: any, index: number) => {
      if (data.isImageURL || data.isVideoURL) {
        return data.dataURL;
      }
      const fileName = formatFileName(data.fileName);
      const blobPath = `${dayjs().format('YYYY')}/${dayjs().format('MM')}/${dayjs().unix()}_${index}_${fileName}`;
      return createBlobInContainer(containerClient, data.blob, blobPath, data.fileType, containerName);
    });

    const data = await Promise.all(promisesUploadImage);

    return data;
  } catch (error) {
    throw new Error('Upload image to azure failed');
  }
};

export const uploadFileAssetsToAzureBlob = async (file: any, containerName?: string) => {
  try {
    if (!file) return false;
    const container: any = containerName || CONFIG_CONTAINER_NAME.AZURE_BLOB_CONTAINER_APP_ASSETS;
    const blobService = new BlobServiceClient(`https://${storageAccountName}.blob.core.windows.net/?${sasToken}`);
    const containerClient = blobService.getContainerClient(container);

    return await createBlobInContainer(containerClient, file.blob, file.path, file.fileType, container);
  } catch (error) {
    throw new Error('Upload image to azure failed');
  }
};

export const getListBlobContainer = async (containerName?: string) => {
  if (!containerName) return [];
  const blobService = new BlobServiceClient(`https://${storageAccountName}.blob.core.windows.net/?${sasToken}`);
  const containerClient = blobService.getContainerClient(containerName);
  const list = [];
  for await (const blob of containerClient.listBlobsFlat()) {
    list.push(blob.name);
  }
  return list;
};

export const toImageUrl = (blob: any) => {
  return `https://${storageAccountName}.blob.core.windows.net/${
    CONFIG_CONTAINER_NAME.AZURE_BLOB_CONTAINER_APP_ASSETS
  }/${blob}?${Math.round(Math.random() * 10000000)}`;
};
